import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Snackbar } from '@mui/material';

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

  const showToast = useCallback((message, severity = 'info') => {
    if (!message) return;
    setToast({ open: true, message: String(message), severity });
  }, []);

  useEffect(() => {
    const onToast = (event) => {
      const { message, severity = 'error' } = event.detail || {};
      showToast(message, severity);
    };
    window.addEventListener('garmenterp:toast', onToast);
    return () => window.removeEventListener('garmenterp:toast', onToast);
  }, [showToast]);

  const value = useMemo(() => ({
    success: (message) => showToast(message, 'success'),
    error: (message) => showToast(message, 'error'),
    warning: (message) => showToast(message, 'warning'),
    info: (message) => showToast(message, 'info'),
    showToast
  }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToast((current) => ({ ...current, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within ToastProvider');
  return context;
}

export function emitToast(message, severity = 'error') {
  window.dispatchEvent(new CustomEvent('garmenterp:toast', { detail: { message, severity } }));
}

export function formatApiError(error, fallback = 'Request failed') {
  const data = error?.response?.data;
  if (!data) return error?.message || fallback;
  if (typeof data.message === 'string' && data.message) {
    if (data.details?.fieldErrors) {
      const fieldMessages = Object.entries(data.details.fieldErrors)
        .flatMap(([field, messages]) => (messages || []).map((message) => `${field}: ${message}`));
      if (fieldMessages.length) return `${data.message}: ${fieldMessages.join(', ')}`;
    }
    return data.debug ? `${data.message} (${data.debug})` : data.message;
  }
  return fallback;
}
